import { Modal } from '@/components/admin/Modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import type { LlmEndpoint } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useMemo, useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  /** 같은 prompt 안에 이미 존재하는 modelMatch 들 — 중복 막기 위해 disable 처리. */
  existingModelMatches: string[];
  isPending: boolean;
  onSubmit: (input: { modelMatch: string; label?: string; temperature?: number }) => void;
}

const MODEL_MATCH_RE = /^[A-Za-z0-9][A-Za-z0-9._:/-]*[A-Za-z0-9]$/;

/**
 * 모델 식별자 + 옵셔널 표시명/temperature 입력. 등록된 LLM 엔드포인트의 unique model 목록을
 * datalist 로 제안 — 사용자가 free text 도 입력 가능 (아직 엔드포인트 미등록 모델 대비).
 */
export function VariantAddModal({
  open,
  onClose,
  existingModelMatches,
  isPending,
  onSubmit,
}: Props) {
  const endpointsQ = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
    enabled: open,
    staleTime: 60_000,
  });

  const [modelMatch, setModelMatch] = useState('');
  const [label, setLabel] = useState('');
  const [temperature, setTemperature] = useState('');

  useEffect(() => {
    if (open) {
      setModelMatch('');
      setLabel('');
      setTemperature('');
    }
  }, [open]);

  const suggestions = useMemo(() => {
    const set = new Set<string>();
    for (const ep of endpointsQ.data?.endpoints ?? ([] as LlmEndpoint[])) {
      if (ep.model && !existingModelMatches.includes(ep.model)) set.add(ep.model);
    }
    return [...set].sort();
  }, [endpointsQ.data, existingModelMatches]);

  const trimmedModel = modelMatch.trim();
  // 빈 입력은 default variant 의 sentinel '' 과 같아 includes() 가 true 가 되는 가양성 — 입력이 있을 때만 검사.
  const isDuplicate = trimmedModel.length > 0 && existingModelMatches.includes(trimmedModel);
  const formatOk = trimmedModel.length === 0 || MODEL_MATCH_RE.test(trimmedModel);

  const tempStr = temperature.trim();
  const tempNum = tempStr === '' ? undefined : Number(tempStr);
  const tempInvalid =
    tempStr !== '' &&
    (tempNum === undefined || !Number.isFinite(tempNum) || tempNum < 0 || tempNum > 2);

  const canSubmit =
    trimmedModel.length > 0 && !isDuplicate && formatOk && !tempInvalid && !isPending;

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      modelMatch: trimmedModel,
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(tempNum !== undefined ? { temperature: tempNum } : {}),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="모델별 variant 추가"
      description="등록된 LLM 엔드포인트의 model 문자열과 정확 일치 시 이 variant 의 본문을 사용합니다."
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={isPending}>
            취소
          </Button>
          <Button type="submit" form="rp-variant-add-form" disabled={!canSubmit}>
            {isPending ? '추가 중…' : '추가'}
          </Button>
        </div>
      }
    >
      <form id="rp-variant-add-form" onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-variant-model">
            모델 식별자
          </label>
          <Input
            id="rp-variant-model"
            value={modelMatch}
            onChange={(e) => setModelMatch(e.target.value)}
            placeholder="예: gemini-3.1-lite 또는 gemma4:26b"
            list="rp-variant-model-suggestions"
            aria-invalid={!formatOk || isDuplicate}
            autoFocus
          />
          <datalist id="rp-variant-model-suggestions">
            {suggestions.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          {!formatOk ? (
            <div className="mt-1 text-xs text-destructive">
              영숫자/점/하이픈/언더스코어/슬래시/콜론만 가능 (시작·끝은 영숫자).
            </div>
          ) : isDuplicate ? (
            <div className="mt-1 text-xs text-destructive">
              이 prompt 에 같은 모델의 variant 가 이미 있습니다.
            </div>
          ) : (
            <div className="mt-1 text-xs text-foreground-muted">
              호출 측 endpoint.model 과 정확 일치할 때만 이 variant 가 선택됩니다 (그 외는 기본).
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs text-foreground-muted mb-1" htmlFor="rp-variant-label">
            표시명 (선택)
          </label>
          <Input
            id="rp-variant-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="예: Gemma 26B"
            maxLength={80}
          />
          <div className="mt-1 text-xs text-foreground-muted">
            비워두면 모델 식별자가 그대로 탭에 표시됩니다.
          </div>
        </div>

        <div>
          <label
            className="block text-xs text-foreground-muted mb-1"
            htmlFor="rp-variant-temperature"
          >
            기본 temperature (선택)
          </label>
          <Input
            id="rp-variant-temperature"
            type="number"
            min={0}
            max={2}
            step={0.01}
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
            placeholder="비워두면 모델/엔드포인트 기본값"
            aria-invalid={tempInvalid}
          />
          {tempInvalid ? (
            <div className="mt-1 text-xs text-destructive">0 과 2 사이 숫자여야 합니다.</div>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
